import { AccessPermissions } from '@root/src/types/common';
import { UserFront } from './types';

/**
 * What a viewer may do, in one place: pure rules over (viewer, subject), bound
 * to the current user by `useViewer`. Every rule mirrors what the backend
 * accepts, with the backend reference next to it; the few the backend does not
 * settle say so. Paths below are in `ch_apptitude_spacetalk_backend`.
 */

/**
 * The level an entity's `access` grants the viewer, as the backend computes it
 * (`accessLevel` on every payload). Mirrors `VISIBILITY_LEVEL`, backend
 * `src/utils/visibility.js:18`.
 */
export const ACCESS_LEVEL = {
  /** Search, discover, reference: some fields only. */
  DISCOVER: 1,
  /** Full entity access: every field, and update. */
  ACCESS: 2,
  /** Entity management: deletion and sharing. */
  MANAGE: 3,
  /** The superuser, on every entity. */
  SUPERUSER: 99,
} as const;

/**
 * What the entity's level alone grants: edit from `ACCESS`, delete from
 * `MANAGE` (backend `withVisibleObject` compares with `$gte`). Routes that
 * also require a role are the viewer rules below.
 */
export const entityAccess = (accessLevel: number | undefined): AccessPermissions => ({
  canEdit: (accessLevel ?? 0) >= ACCESS_LEVEL.ACCESS,
  canDelete: (accessLevel ?? 0) >= ACCESS_LEVEL.MANAGE,
});

/**
 * The user looking at the page, as the backend sees them: `admin` is the JWT
 * flag `isAdmin` checks, `superuser` the account `withSuperUser` checks.
 * `undefined` while the current user is loading: every capability is then
 * refused, and nobody is taken for an observer.
 */
export type Viewer = {
  userId: string;
  memberId?: string;
  admin: boolean;
  superuser: boolean;
};

export const toViewer = (
  user: Pick<UserFront, 'id' | 'admin' | 'superuser' | 'member'> | undefined,
): Viewer | undefined =>
  user && {
    userId: user.id,
    memberId: user.member?.id,
    admin: user.admin === true,
    superuser: user.superuser === true,
  };

/**
 * `isAdmin`, backend `src/middleware/isAdmin.js:4-6`: the JWT `admin` flag.
 * The superuser account is seeded `admin: true` (`seeds/seedAdmin.js:20`) and
 * cannot be modified (`users/controller.js:23`).
 */
const isAdmin = (viewer: Viewer | undefined): viewer is Viewer => viewer?.admin === true;

/** `withSuperUser`, backend `src/middleware/withSuperUser.js:4-8`. */
const isSuperuser = (viewer: Viewer | undefined): viewer is Viewer => viewer?.superuser === true;

/**
 * The superuser watches, they do not take part: the backend refuses them the
 * creation of SSTs and dedicated forums ("Super user shall not create items",
 * backend `src/models/plugins/accessContext.js:72-75`, called from
 * `ssts/controller.js:309` and `dedicated-forums/controller.js:190`).
 */
export const isObserver = (viewer: Viewer | undefined): boolean => isSuperuser(viewer);

/** Publishing an SST, from anywhere it is offered. */
export const canPublishSst = (viewer: Viewer | undefined): boolean => !isObserver(viewer);

/** Creating a dedicated forum. */
export const canCreateDedicatedForum = (viewer: Viewer | undefined): boolean => !isObserver(viewer);

/**
 * Adding a space object: `POST /spaceobjects` takes any admin (backend
 * `spaceObjects/router.js:29`), and the object always lands in the admin's own
 * entity (`createWithDefaultAccess`, `models/plugins/accessContext.js:77-81`).
 * So on an entity's page (`toMemberId`) it is offered only when that entity is
 * the admin's own; elsewhere (the inventory) to any admin.
 */
export const canAddSpaceObject = (viewer: Viewer | undefined, toMemberId?: string): boolean =>
  isAdmin(viewer) && (toMemberId === undefined || toMemberId === viewer.memberId);

/**
 * Editing an entity: `PUT /members/:id` takes an admin (`isAdmin`) with the
 * access level on it (backend `members/router.js:79-80`). The level alone,
 * `member.canEdit`, is not enough (BUG-148).
 */
export const canEditMember = (viewer: Viewer | undefined, member: Partial<AccessPermissions> | undefined): boolean =>
  isAdmin(viewer) && member?.canEdit === true;

/**
 * Editing a space object: `PUT /spaceobjects/:id` takes an admin (`isAdmin`)
 * with the access level on it (backend `spaceObjects/router.js:94-95`). The
 * level alone, `spaceObject.canEdit`, is not enough.
 */
export const canEditSpaceObject = (
  viewer: Viewer | undefined,
  spaceObject: Partial<AccessPermissions> | undefined,
): boolean => isAdmin(viewer) && spaceObject?.canEdit === true;

/**
 * Deleting a space object: `DELETE /spaceobjects/:id` takes an admin
 * (`isAdmin`) with the manage level on it (backend `spaceObjects/router.js:109-110`).
 * The level alone, `spaceObject.canDelete`, is not enough.
 */
export const canDeleteSpaceObject = (
  viewer: Viewer | undefined,
  spaceObject: Partial<AccessPermissions> | undefined,
): boolean => isAdmin(viewer) && spaceObject?.canDelete === true;

/**
 * Selecting a space object for a bulk edit (the inventory's bulk point of
 * contact assignment): `PUT /spaceobjects/bulk-edit` is behind `isAdmin`
 * (backend `spaceObjects/router.js:83`), then silently skips the objects below
 * the access level (`spaceObjects/controller.js:1154`), so those are not offered.
 */
export const canBulkEditSpaceObjects = (
  viewer: Viewer | undefined,
  spaceObject: Partial<AccessPermissions> | undefined,
): boolean => isAdmin(viewer) && spaceObject?.canEdit === true;

/**
 * Deleting an entity: `DELETE /members/:id` is behind `withSuperUser`, then
 * needs the manage level on it (backend `members/router.js:92-94`). The level
 * alone, `member.canDelete`, is not enough.
 */
export const canDeleteMember = (viewer: Viewer | undefined, member: Partial<AccessPermissions> | undefined): boolean =>
  isSuperuser(viewer) && member?.canDelete === true;

/** Creating an entity: `POST /members` is behind `withSuperUser` (backend `members/router.js:27`). */
export const canCreateMember = (viewer: Viewer | undefined): boolean => isSuperuser(viewer);

/** Adding a point of contact: `POST /users` is behind `isAdmin` (backend `users/router.js:22`). */
export const canAddPointOfContact = (viewer: Viewer | undefined): boolean => isAdmin(viewer);

type Contact = Pick<UserFront, 'id'> & { member?: { id?: string } };

/**
 * Editing a point of contact: `PUT /users/:id` takes an admin with the access
 * level on the user (backend `users/router.js:112-113`). A user is created with
 * that level for their own entity (`users/controller.js` `createUser`), and the
 * superuser has it on everyone.
 */
export const canEditPoC = (viewer: Viewer | undefined, contact: Contact | undefined): boolean =>
  isAdmin(viewer) &&
  contact !== undefined &&
  (viewer.superuser || (viewer.memberId !== undefined && contact.member?.id === viewer.memberId));

/** Deleting a user: `DELETE /users/:id` is behind `withSuperUser` (backend `users/router.js:133`); never oneself. */
export const canDeleteUser = (viewer: Viewer | undefined, target: Pick<UserFront, 'id'>): boolean =>
  isSuperuser(viewer) && target.id !== viewer.userId;

/**
 * Changing an SST's visibility (public, or shared with chosen users):
 * `PUT /ssts/:id` needs the access level (backend `ssts/router.js:100`), then
 * refuses anyone without the manage level on the SST, for their user or their
 * entity (`ssts/controller.js:469-483`). Only the author is offered it.
 */
export const canChangeSstVisibility = (
  viewer: Viewer | undefined,
  sst: { createdBy: { userId: string }; accessLevel: number },
): boolean => viewer !== undefined && sst.createdBy.userId === viewer.userId && sst.accessLevel >= ACCESS_LEVEL.MANAGE;

/** The admin area (events, inventory health, migrations, wipe): every `/admin` route is behind `withSuperUser` (backend `admin/router.js`). */
export const canUseAdminArea = (viewer: Viewer | undefined): boolean => isSuperuser(viewer);

/** The inventory CSV export: `GET /spaceobjects/export` is behind `withSuperUser` (backend `spaceObjects/router.js:51`). */
export const canExportSpaceObjects = (viewer: Viewer | undefined): boolean => isSuperuser(viewer);

/**
 * Deleting an SST: offered to the superuser only. The backend is wider,
 * `DELETE /ssts/:id` takes an admin or the superuser with the manage level
 * (`ssts/router.js:112-113`); whether admins should get the button is open.
 */
export const canDeleteSst = (viewer: Viewer | undefined): boolean => isSuperuser(viewer);

/**
 * Giving an entity the administrator type: offered to the superuser only. A
 * frontend rule: the backend validators accept the type from any editor.
 */
export const canChooseAdministratorType = (viewer: Viewer | undefined): boolean => isSuperuser(viewer);

/** A point of contact's email and language, on their page: shown to the superuser only. A frontend rule. */
export const canSeeContactDetails = (viewer: Viewer | undefined): boolean => isSuperuser(viewer);
